/**
 * 캘린더 반복 일정 (RRULE) 유틸
 *
 * - Google Calendar 패턴의 프리셋 (안 함 / 매일 / 평일 / 매주 / 매월 N일 / 매월 N번째 요일 / 매년 / 커스텀)
 * - 시작일을 기준으로 동적 라벨 생성 ("매월 두번째 월요일" 등)
 * - rrule.js 로 RRULE 문자열 ↔ 인스턴스 전개
 *
 * RRULE 형식: "FREQ=WEEKLY;BYDAY=MO,WE;COUNT=10"
 * Phase 1 은 RRULE 문자열만 저장 (DTSTART 는 events.start_at 으로 별도 보관)
 */
import { RRule, Frequency, Weekday } from 'rrule';
import { t } from '@lingui/macro';
import { i18n } from '@lingui/core';

/** 현재 locale 의 요일명 (0=일 ~ 6=토). long/short */
function localeWeekday(dayIdx: number, style: 'long' | 'short' = 'long'): string {
  // 2024-01-07 = 일요일 기준
  const d = new Date(2024, 0, 7 + dayIdx);
  try {
    return new Intl.DateTimeFormat(i18n.locale || 'en', { weekday: style }).format(d);
  } catch {
    return ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][dayIdx];
  }
}

/** 현재 locale 의 서수 라벨 (1~5번째). nthName 자리에 들어감 */
function localeNth(n: number): string {
  const loc = i18n.locale || 'en';
  if (loc.startsWith('ja')) return `第${n}`;
  if (loc.startsWith('ko')) return `${n}번째`;
  return (['1st', '2nd', '3rd', '4th', '5th'][n - 1]) ?? `${n}th`;
}

export type RecurrencePreset =
  | 'none'
  | 'daily'
  | 'weekdays'
  | 'weekly'
  | 'monthly_date'         // 매월 N일
  | 'monthly_nth_weekday'  // 매월 N번째 요일
  | 'yearly'
  | 'custom';

const RRULE_WEEKDAYS = [RRule.SU, RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR, RRule.SA];

/** 시작일 기준 — 그 날이 그 월의 몇 번째 요일인지 (1~5) */
export function nthWeekdayOfMonth(date: Date): number {
  return Math.ceil(date.getDate() / 7);
}

/** 프리셋의 동적 라벨 (시작일 기준) */
export function recurrencePresetLabel(preset: RecurrencePreset, startDate: Date): string {
  const dayName = localeWeekday(startDate.getDay());
  const monthDay = startDate.getDate();
  const month = startDate.getMonth() + 1;
  const nth = nthWeekdayOfMonth(startDate);
  const nthName = localeNth(nth);
  switch (preset) {
    case 'none':                 return t`Does not repeat`;
    case 'daily':                return t`Daily`;
    case 'weekdays':             return t`Weekdays (Mon–Fri)`;
    case 'weekly':               return t`Every ${dayName}`;
    case 'monthly_date':         return t`Monthly on day ${monthDay}`;
    case 'monthly_nth_weekday':  return t`${nthName} ${dayName} of each month`;
    case 'yearly':               return t`Yearly on ${month}/${monthDay}`;
    case 'custom':               return t`Custom…`;
  }
}

/**
 * 프리셋 → RRULE 문자열 (DTSTART 포함하지 않음, FREQ 기반만)
 * 시작일 기준으로 BYDAY/BYMONTHDAY 등을 자동 설정.
 */
export function presetToRRule(preset: RecurrencePreset, startDate: Date): string | null {
  if (preset === 'none' || preset === 'custom') return null;
  const dow = startDate.getDay(); // 0=Sun
  const weekday = RRULE_WEEKDAYS[dow];
  const monthDay = startDate.getDate();
  const month = startDate.getMonth() + 1;
  const nth = nthWeekdayOfMonth(startDate);

  switch (preset) {
    case 'daily':
      return 'FREQ=DAILY';
    case 'weekdays':
      return 'FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR';
    case 'weekly':
      return `FREQ=WEEKLY;BYDAY=${weekday.toString()}`;
    case 'monthly_date':
      return `FREQ=MONTHLY;BYMONTHDAY=${monthDay}`;
    case 'monthly_nth_weekday':
      return `FREQ=MONTHLY;BYDAY=${nth}${weekday.toString()}`;
    case 'yearly':
      return `FREQ=YEARLY;BYMONTH=${month};BYMONTHDAY=${monthDay}`;
  }
  return null;
}

/** RRULE 문자열 → 사람이 읽을 수 있는 영어 요약 (간단 케이스만) */
export function describeRRule(rrule: string | null | undefined, startDate: Date): string {
  if (!rrule) return t`Does not repeat`;
  try {
    const rule = RRule.fromString(`RRULE:${rrule}`);
    const o = rule.origOptions;
    const dayName = localeWeekday(startDate.getDay());
    const monthDay = startDate.getDate();
    const month = startDate.getMonth() + 1;

    switch (o.freq) {
      case Frequency.DAILY:
        return t`Daily`;
      case Frequency.WEEKLY: {
        const days = (o.byweekday as Weekday[] | number[] | null | undefined);
        if (!days || (Array.isArray(days) && days.length === 0)) return t`Every ${dayName}`;
        const arr = Array.isArray(days) ? days : [days];
        if (arr.length === 5) {
          // 평일 추정 (정확하진 않지만 휴리스틱)
          return t`Weekdays (Mon–Fri)`;
        }
        if (arr.length === 1) return t`Every ${dayName}`;
        const names = arr.map((d) => {
          const num = typeof d === 'number' ? d : (d as Weekday).weekday;
          // rrule weekday 는 MO=0, SU=6
          const jsIdx = (num + 1) % 7;
          return localeWeekday(jsIdx, 'short');
        }).join(', ');
        return t`Weekly on ${names}`;
      }
      case Frequency.MONTHLY: {
        if (o.bymonthday) return t`Monthly on day ${monthDay}`;
        if (o.byweekday) {
          const arr = Array.isArray(o.byweekday) ? o.byweekday : [o.byweekday];
          const first = arr[0] as any;
          if (first && typeof first === 'object' && typeof first.n === 'number') {
            const nth = first.n;
            const nthName = localeNth(nth);
            return t`${nthName} ${dayName} of each month`;
          }
        }
        return t`Monthly`;
      }
      case Frequency.YEARLY:
        return t`Yearly on ${month}/${monthDay}`;
      default:
        return t`Custom`;
    }
  } catch {
    return t`Custom`;
  }
}

/** Date → RRULE DTSTART 포맷 (UTC, 형식: 20260522T130000Z) */
function toDtstartFormat(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}Z`;
}

/**
 * 반복 일정의 인스턴스 전개 — range 안에 떨어지는 occurrence 들의 시작 시각 배열
 * DTSTART 를 RRULE 문자열에 포함시켜 fromString 으로 파싱.
 */
export function expandOccurrences(params: {
  rrule: string;
  dtstart: Date;
  rangeStart: Date;
  rangeEnd: Date;
}): Date[] {
  try {
    const dtstartStr = toDtstartFormat(params.dtstart);
    const ruleStr = `DTSTART:${dtstartStr}\nRRULE:${params.rrule}`;
    const rule = RRule.fromString(ruleStr);
    return rule.between(params.rangeStart, params.rangeEnd, true);
  } catch (err) {
    console.error('[recurrence] expandOccurrences 실패:', err, params.rrule);
    return [];
  }
}
